import api, { API_BASE } from './api';
import {
    ApiEnvelope,
    AssignmentDetail,
    AssignCoursePayload,
    CategoryPayload,
    Course,
    CourseCategory,
    CourseCertificate,
    CourseFormOptions,
    CoursePayload,
    LearningNotificationSettings,
    LibraryCourse,
    MyLearningResponse,
    OnboardingLearningDetail,
    OnboardingLearningProgress,
    PersonLearningResponse,
    RegisterResponse,
    ScormSession,
} from '../types/learning.types';

/**
 * Learning module API (LEARNING.md §5.1, §7.4).
 *
 * Thin wrapper over utils/api, mirroring utils/stock-api.ts: the shared `api` helper
 * already handles the base URL, the Bearer token, JSON parsing, toasts and the global 401
 * event, so nothing here re-implements transport.
 *
 * Route layout mirrors the backend mounts — categories, courses, assignments and the
 * learner surface all hang off /api/learning.
 */

const query = (params: Record<string, string | number | boolean | undefined>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== false) q.append(key, String(value));
    });
    const s = q.toString();
    return s ? `?${s}` : '';
};

/** Shape returned by backend/routes/learningUpload.routes.js (and every S3 uploader). */
export interface UploadResult {
    url: string;
    key: string;
    filename: string;
    size: number;
    mimetype: string;
    /**
     * A freshly signed GET for immediate display. `url` is the canonical, non-expiring
     * reference that gets PERSISTED; the bucket is private, so showing it directly would 403
     * until the next read re-signs it. Only the thumbnail endpoint returns this.
     */
    previewUrl?: string;
}

/** Export is a file download, so it needs the raw URL rather than an api.get(). */
export const learningExportUrl = (
    params: { search?: string; categoryId?: number; status?: string; format?: 'csv' | 'excel' },
) => `${API_BASE.replace(/\/$/, '')}/api/learning/courses/export${query(params)}`;

export const learningApi = {
    categories: {
        list: () => api.get<CourseCategory[]>('/api/learning/categories'),
        create: (body: CategoryPayload) => api.post<CourseCategory>('/api/learning/categories', body),
        update: (id: number, body: Partial<CategoryPayload>) =>
            api.patch<CourseCategory>(`/api/learning/categories/${id}`, body),
        archive: (id: number) => api.post<CourseCategory>(`/api/learning/categories/${id}/archive`, {}),
        restore: (id: number) => api.post<CourseCategory>(`/api/learning/categories/${id}/restore`, {}),
    },

    courses: {
        list: (params: { search?: string; categoryId?: number; status?: string } = {}) =>
            api.get<Course[]>(`/api/learning/courses${query(params)}`),
        options: () => api.get<CourseFormOptions>('/api/learning/courses/options'),
        get: (id: number) => api.get<Course>(`/api/learning/courses/${id}`),
        create: (body: CoursePayload) => api.post<Course>('/api/learning/courses', body),
        update: (id: number, body: Partial<CoursePayload>) =>
            api.patch<Course>(`/api/learning/courses/${id}`, body),
        publish: (id: number) => api.post<Course>(`/api/learning/courses/${id}/publish`, {}),
        archive: (id: number) => api.post<Course>(`/api/learning/courses/${id}/archive`, {}),
        remove: (id: number) => api.delete<{ id: number; deleted: boolean }>(`/api/learning/courses/${id}`),
    },

    assignments: {
        list: (params: { courseId?: number; department?: string; status?: string; overdue?: boolean } = {}) =>
            api.get<RegisterResponse>(`/api/learning/assignments${query(params)}`),
        detail: (courseId: number) => api.get<AssignmentDetail>(`/api/learning/assignments/${courseId}`),
        create: (body: AssignCoursePayload) =>
            api.post<{ courseId: number; created: number; skipped: number }>('/api/learning/assignments', body),
        setPeople: (courseId: number, mode: 'add' | 'remove', userIds: number[]) =>
            api.patch<{ created?: number; removed?: number; skipped?: number; locked?: number }>(
                `/api/learning/assignments/${courseId}/people`, { mode, userIds }),
        remove: (id: number) =>
            api.delete<{ id: number; deleted: boolean }>(`/api/learning/assignments/${id}`),
    },

    learner: {
        my: () => api.get<MyLearningResponse>('/api/learning/my'),
        library: () => api.get<LibraryCourse[]>('/api/learning/library'),
        enroll: (courseId: number) => api.post<unknown>('/api/learning/enrollments', { courseId }),
        start: (courseId: number) =>
            api.post<unknown>(`/api/learning/enrollments/${courseId}/progress`, { action: 'start' }),
        complete: (courseId: number) =>
            api.post<unknown>(`/api/learning/enrollments/${courseId}/progress`, { action: 'complete' }),
        /**
         * SCORM only: mints one play-through. `launchUrl` is relative to the API origin, which
         * serves the package and injects the runtime shim — the content is never loaded
         * straight from S3 (LEARNING.md §13.1). `startFresh` is the learner's answer to the
         * player's resume question (§17.10).
         */
        scormSession: (courseId: number, startFresh = false) =>
            api.post<ScormSession>('/api/learning/scorm/session', { courseId, startFresh }),
        certificates: () => api.get<CourseCertificate[]>('/api/learning/certificates'),
        certificate: (id: number) => api.get<CourseCertificate>(`/api/learning/certificates/${id}`),
    },

    /**
     * Onboarding-embedded learning. A hire's mandatory pre-start courses are addressed through
     * their onboarding request rather than a course assignment, so they are their own group —
     * and unlike the learning routes above these endpoints wrap their payload in an envelope.
     */
    onboarding: {
        course: (courseId: number) =>
            api.get<ApiEnvelope<OnboardingLearningDetail>>(
                `/api/onboarding/my-portal/learning/${courseId}`,
            ).then((res) => res.data),
        session: (courseId: number, startFresh = false) =>
            api.post<ApiEnvelope<ScormSession>>(
                `/api/onboarding/my-portal/learning/${courseId}/session`,
                { startFresh },
            ).then((res) => res.data),
        progress: (courseId: number, action: 'start' | 'complete') =>
            api.post<ApiEnvelope<OnboardingLearningProgress>>(
                `/api/onboarding/my-portal/learning/${courseId}/progress`,
                { action },
            ).then((res) => res.data),
    },

    person: {
        get: (userId: string | number) =>
            api.get<PersonLearningResponse>(`/api/learning/person/${userId}`),
    },

    /** Notification configuration (LEARNING.md §16) — the Settings page. */
    settings: {
        get: () => api.get<LearningNotificationSettings>('/api/learning/settings'),
        update: (body: LearningNotificationSettings) =>
            api.put<LearningNotificationSettings>('/api/learning/settings', body),
    },

    /**
     * File uploads. Returns the S3 { url, key } which the caller then sends on the course
     * create/update payload — the file has to exist before the course can point at it, so
     * this is a separate step rather than a side effect of the save.
     */
    uploads: {
        content: (file: File, courseId?: number) => {
            const form = new FormData();
            form.append('file', file);
            if (courseId) form.append('courseId', String(courseId));
            return api.post<UploadResult>('/api/learning/uploads/content', form);
        },
        thumbnail: (file: File, courseId?: number) => {
            const form = new FormData();
            form.append('image', file);
            if (courseId) form.append('courseId', String(courseId));
            return api.post<UploadResult>('/api/learning/uploads/thumbnail', form);
        },
    },
};

export default learningApi;
